<template>
    <div
        :class="[
            {
                'grid gap-3 auto-rows-fr grid-cols-3 sm:grid-cols-4':
                    hasCardStyleModifier
            }
        ]"
        class="-mx-2"
    >
        <div
            v-for="(option, index) in options"
            :key="getOptionKey(option, index)"
            class="relative flex w-full items-center mb-1"
        >
            <label
                :aria-label="getOptionLabel(option)"
                :class="[
                    {
                        'h-full': hasCardStyleModifier,
                        'px-2 bg-transparent': !hasCardStyleModifier,
                        checked: isSelected(option),
                        'rounded-xl ring-2 ring-gray-700 bg-modifier-selectedcard shadow-modifier':
                            isSelected(option) && hasCardStyleModifier,
                        'rounded-xl ring-1 ring-gray-200 bg-modifier-card shadow-modifier':
                            !isSelected(option) && hasCardStyleModifier
                    }
                ]"
                :for="getCheckboxId(option)"
                class="sk-checkbox no-highlight relative flex w-full items-center mb-1 rounded outline-none cursor-pointer select-none"
                role="button"
                tabindex="0"
            >
                <input
                    :id="getCheckboxId(option)"
                    :checked="isSelected(option)"
                    :disabled="isDisabled(option)"
                    :value="option[valueField]"
                    class="custom-control-input form-check-input form-checkbox absolute w-4 h-4 text-gray-700 opacity-0 transition duration-150 ease-in-out"
                    type="checkbox"
                    @change="onChange(option)"
                />
                <div
                    v-if="hasCardStyleModifier"
                    :class="{ 'opacity-50 cursor-default': isDisabled(option) }"
                    class="p-3 pb-6 relative flex gap-0 items-center h-full justify-between flex-col"
                >
                    <button
                        v-if="hasAllergens(option) || option.description"
                        class="absolute top-[5px] right-[5px] hover:drop-shadow"
                        @click.stop="
                            $modal.show('modifier-extra-info-modal', {
                                item: option.name,
                                text: getExtraInfoText(option)
                            })
                        "
                    >
                        <info-icon class="size-4" />
                    </button>
                    <div
                        :class="{
                            'pr-2': hasAllergens(option) || option.description
                        }"
                        class="flex w-full"
                    >
                        <header
                            class="line-clamp-2 w-full text-xs text-primary sm:text-sm text-center break-words"
                        >
                            {{ getOptionLabel(option) }}
                        </header>
                    </div>

                    <div class="size-[95px]">
                        <img
                            v-if="option.modifierImage"
                            :alt="getOptionLabel(option)"
                            :src="getOptimizedImageSrc(option.modifierImage)"
                            :srcset="
                                getOptimizedImageSrcset(option.modifierImage)
                            "
                            class="size-full p-2 object-contain transition-all duration-150 ease-in-out"
                            decoding="async"
                            loading="lazy"
                        />
                    </div>
                    <p
                        v-if="
                            option.price && (showPrices || isPaidOption(option))
                        "
                        class="absolute inset-x-0 pb-1 text-center text-primary bottom-0 text-price text-xs"
                    >
                        <span v-if="option.price > 0">{{ pricePrefix }}</span>
                        {{ (option.price / 100) | customCurrency }}
                        <s
                            v-if="option.compareAtPrice"
                            class="ml-1 text-gray-400"
                        >
                            {{ pricePrefix
                            }}{{
                                (option.compareAtPrice / 100) | customCurrency
                            }}
                        </s>
                    </p>
                </div>
                <div
                    v-else
                    :class="{ 'opacity-50': isDisabled(option) }"
                    class="custom-checkbox z-10 flex items-center p-2 pl-0 w-full rounded"
                >
                    <modifier-image
                        v-if="option.modifierImage"
                        :alt="getOptionLabel(option)"
                        :image="option.modifierImage"
                        :is-selected="isSelected(option)"
                    />
                    <span
                        v-if="!visualMenu && !option.modifierImage"
                        class="custom-checkbox-check mt-[2px] self-start"
                    />
                    <div class="custom-checkbox-align">
                        <div class="pl-2 text-itembody text-sm">
                            {{ getOptionLabel(option) }}
                            <div class="flex">
                                <span
                                    v-if="option.nutrition?.calories"
                                    class="block whitespace-pre-line mr-1 text-sm text-muted"
                                >
                                    {{ option.nutrition.calories }} kcal
                                </span>
                                <span
                                    v-if="
                                        !isUnavailable(option) &&
                                        hasModifierAllergenDisplay &&
                                        hasAllergens(option)
                                    "
                                    class="block whitespace-pre-line text-sm text-muted mr-1"
                                >
                                    {{ getAllergenText(option) }}
                                </span>
                                <span
                                    v-if="isUnavailable(option)"
                                    class="text-red-600"
                                    >{{
                                        $t('checkbox.currentlyUnavailable')
                                    }}</span
                                >
                                <span
                                    v-if="option.popular && option.isAvailable"
                                    class="flex items-center text-orange-400 text-xs"
                                >
                                    <star-icon class="mr-0.5 w-4 h-4" />
                                    {{ $t('menuItem.popular') }}
                                </span>
                            </div>
                        </div>

                        <div class="flex items-center justify-between">
                            <div>
                                <p
                                    v-if="
                                        option.price &&
                                        (showPrices || isPaidOption(option))
                                    "
                                    class="shrink-0 whitespace-nowrap pl-2 text-price text-sm"
                                >
                                    <span v-if="option.price > 0">{{
                                        pricePrefix
                                    }}</span>
                                    {{ (option.price / 100) | customCurrency }}

                                    <s
                                        v-if="option.compareAtPrice"
                                        class="ml-1 text-gray-400"
                                    >
                                        {{ pricePrefix
                                        }}{{
                                            (option.compareAtPrice / 100)
                                                | customCurrency
                                        }}
                                    </s>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </label>
            <QuantityStepper
                v-if="isSelected(option) && multiMax > 1"
                :disabled="isInputDisabled"
                :is-card-style="hasCardStyleModifier"
                :multi-max="multiMax"
                :value="getOptionQuantity(option)"
                @input="val => setOptionQuantity(option, val)"
            />
        </div>
    </div>
</template>
<script>
import { mapState } from 'vuex';
import isMobile from '@/helpers/isMobileDevice';
import { processItemAllergens } from '@/helpers/allergens';
import InfoIcon from '@/components/icons/InfoIcon';
import StarIcon from '@/components/icons/StarIcon';
import ModifierImage from '@/components/ModifierImage.vue';
import QuantityStepper from '@/components/formElements/QuantityStepper.vue';

export default {
    name: 'checkboxInput',
    props: {
        options: {
            type: Array,
            required: true
        },
        value: {
            type: Array,
            required: false,
            default: () => []
        },
        optionGroupId: {
            type: String,
            required: false,
            default: ''
        },
        labelField: {
            type: String,
            required: false,
            default: 'label'
        },
        valueField: {
            type: String,
            required: false,
            default: 'value'
        },
        max: {
            type: Number,
            required: false,
            default: null
        },
        itemPrice: {
            type: Number,
            required: false
        },
        multiMax: {
            type: Number,
            required: false,
            default: 1
        },
        showPrices: {
            type: Boolean,
            required: false,
            default: true
        },
        paidOptionIds: {
            type: Array,
            required: false,
            default: () => []
        }
    },
    components: {
        QuantityStepper,
        ModifierImage,
        InfoIcon,
        StarIcon
    },
    methods: {
        isSelected(option) {
            return this.selectedItems.some(
                itm => itm[this.valueField] === option[this.valueField]
            );
        },
        isUnavailable(option) {
            return option.snooze || option.isAvailable === false;
        },
        getCheckboxId(option) {
            return `checkbox-${this.optionGroupId}-${option[this.valueField]}`;
        },
        getOptionKey(option, index) {
            return `${this.optionGroupId}-${option[this.valueField]}-${index}`;
        },
        getOptionLabel(option) {
            return option[this.labelField];
        },
        isDisabled(option) {
            return (
                !this.isSelected(option) &&
                (this.isInputDisabled || this.isUnavailable(option))
            );
        },
        hasAllergens(option) {
            const allergenInfo = processItemAllergens(
                option,
                [],
                this.$t('allergens')
            );
            return (
                allergenInfo.allergens.length > 0 ||
                allergenInfo.possibleAllergens.length > 0
            );
        },
        getAllergenText(option) {
            const { allergens, possibleAllergens } = processItemAllergens(
                option,
                [],
                this.$t('allergens')
            );

            const allergenText = [];

            if (allergens.length > 0) {
                allergenText.push(
                    `Contains allergens: ${allergens.join(', ')}`
                );
            }

            if (possibleAllergens.length > 0) {
                allergenText.push(
                    `May contain allergens: ${possibleAllergens.join(', ')}`
                );
            }

            return allergenText.join(' • ');
        },
        getExtraInfoText(option) {
            const allergenText = this.getAllergenText(option);
            const { description = '' } = option;

            if (description && allergenText) {
                return `${description}\n\n${allergenText}`;
            }

            return description || allergenText;
        },
        getOptionQuantity(option) {
            const itm = this.selectedItems.find(
                x => x[this.valueField] === option[this.valueField]
            );
            return itm ? itm.quantity : 0;
        },
        setOptionQuantity(option, newQty) {
            if (newQty > this.effectiveMax) return;

            const key = this.valueField;
            const itmIndex = this.selectedItems.findIndex(
                i => i[key] === option[key]
            );

            if (!~itmIndex) return;

            if (newQty === 0) {
                return this.selectedItems.splice(itmIndex, 1);
            }

            this.selectedItems[itmIndex].quantity = newQty;
        },
        getOptimizedImageSrc(imageUrl) {
            return `${imageUrl}/-/format/auto/`;
        },
        getOptimizedImageSrcset(imageUrl) {
            return `${imageUrl}/-/format/auto/-/resize/128x/ 1x, ${imageUrl}/-/format/auto/-/resize/192x/ 2x, ${imageUrl}/-/format/auto/-/resize/256x/ 3x`;
        },
        onChange(option) {
            const key = this.valueField;
            const idx = this.selectedItems.findIndex(
                i => i[key] === option[key]
            );

            if (idx === -1) {
                this.selectedItems.push({
                    [key]: option[key],
                    quantity: 1,
                    subModifierGroups: option['subModifierGroups']
                });
            } else {
                // When card style modifiers are active with multi max > 1
                // and selection quantity > 1, don't deselect - let the user
                // use the quantity stepper instead
                if (
                    this.hasCardStyleModifier &&
                    this.multiMax > 1 &&
                    this.selectedItems[idx].quantity > 1
                ) {
                    return;
                }
                this.selectedItems.splice(idx, 1);
            }

            this.$emit('input', this.selectedItems);
        },
        isPaidOption(option) {
            const id = option[this.valueField];
            return this.paidOptionIds.includes(id);
        }
    },
    computed: {
        ...mapState({
            visualMenu: state => state.venue.venue.visualMenu
        }),
        selectedItems: {
            get() {
                return this.value;
            },
            set(items) {
                this.$emit('input', items);
            }
        },
        effectiveMax() {
            return this.max || Math.max(this.multiMax, 99);
        },
        pricePrefix() {
            return this.itemPrice === 0 ? '' : '+';
        },
        hasModifierAllergenDisplay() {
            return this.$growthbook.isOn('modifier_allergens');
        },
        hasCardStyleModifier() {
            const modifierStyle = this.$store.state.venue.venue.modifierStyle;

            if (modifierStyle === 'all_cards') {
                return true;
            }

            if (modifierStyle === 'cards_all_images') {
                return (
                    this.options?.length > 0 &&
                    this.options.every(option => option.modifierImage)
                );
            }

            return false;
        },
        isMobile() {
            return isMobile();
        },
        isInputDisabled() {
            if (!this.max) {
                return false;
            }

            if (this.multiMax <= 1) {
                return this.selectedItems.length >= this.max;
            }

            const sumQuantity = this.selectedItems.reduce(
                (sum, { quantity = 0 }) => sum + quantity,
                0
            );

            return sumQuantity >= this.effectiveMax;
        }
    }
};
</script>
